// Assertion engine — implements Tier 1 (path), Tier 2 (boundary crossing),
// Tier 3 (schema contract), branch evaluation, and terminal state assertions.
//
// Each tier is a pure function returning assertion results. The walker saga
// calls these after each step. The full-sequence path check runs at completion.

import type { PlatformEvent, ConnectionDef, NodeDef, BranchPredicate } from '../../types/index.ts';
import type { AssertionType } from '../../types/simulation-results.ts';

export interface AssertionOutput {
  assertionId: string;
  assertionType: AssertionType;
  passed: boolean;
  /**
   * True when the assertion could not be evaluated (missing artifact data,
   * unresolvable connection, resolution fallback, …). `passed` is false for
   * skipped records; the reason lives in `detail`. Skips are counted
   * separately in summaries — never as passes or failures.
   */
  skipped?: boolean;
  detail: string;
}

/** Build an explicit SKIPPED assertion record (visible non-evaluation). */
export function skipAssertion(
  assertionId: string,
  assertionType: AssertionType,
  reason: string,
): AssertionOutput {
  return { assertionId, assertionType, passed: false, skipped: true, detail: reason };
}

/** The branch marker suffix (`brN` / `main`) of a full Moment node ID, or null. */
export function branchMarkerOf(nodeId: string): string | null {
  const idx = nodeId.lastIndexOf('::');
  if (idx === -1) return null;
  return nodeId.slice(idx + 2);
}

// ---------------------------------------------------------------------------
// Tier 1 — Path Assertions
// ---------------------------------------------------------------------------

/** Per-step: does the actual node match the expected node at this index? */
export function assertPathStep(
  stepIndex: number,
  actualNodeId: string,
  expectedPath: string[],
): AssertionOutput {
  const assertionId = `path-step-${stepIndex}`;
  if (stepIndex >= expectedPath.length) {
    return {
      assertionId,
      assertionType: 'path',
      passed: false,
      detail: `Step ${stepIndex}: beyond expected path length (${expectedPath.length})`,
    };
  }
  const expected = expectedPath[stepIndex];
  if (actualNodeId === expected) {
    return {
      assertionId,
      assertionType: 'path',
      passed: true,
      detail: `Step ${stepIndex}: "${actualNodeId}" matches expected`,
    };
  }
  return {
    assertionId,
    assertionType: 'path',
    passed: false,
    detail: `Step ${stepIndex}: expected "${expected}" but got "${actualNodeId}"`,
  };
}

/** Terminal: does the full actual path match the full expected path? */
export function assertPathSequence(actualPath: string[], expectedPath: string[]): AssertionOutput {
  const match =
    actualPath.length === expectedPath.length && actualPath.every((n, i) => n === expectedPath[i]);

  if (match) {
    return {
      assertionId: 'path-sequence',
      assertionType: 'path',
      passed: true,
      detail: `Full path match: ${actualPath.length} nodes`,
    };
  }

  // Find first divergence
  let divergeIndex = 0;
  while (
    divergeIndex < actualPath.length &&
    divergeIndex < expectedPath.length &&
    actualPath[divergeIndex] === expectedPath[divergeIndex]
  ) {
    divergeIndex++;
  }

  return {
    assertionId: 'path-sequence',
    assertionType: 'path',
    passed: false,
    detail: `Path diverges at index ${divergeIndex}: expected "${expectedPath[divergeIndex] ?? 'END'}" got "${actualPath[divergeIndex] ?? 'END'}" (actual=${actualPath.length} expected=${expectedPath.length})`,
  };
}

// ---------------------------------------------------------------------------
// Tier 2 — Boundary Crossing Assertions
// ---------------------------------------------------------------------------

/** Validates cross-boundary connections have proper causation and productSource. */
export function assertBoundaryCrossing(
  stepIndex: number,
  event: PlatformEvent,
  previousEvent: PlatformEvent | null,
  connection: ConnectionDef | null,
): AssertionOutput[] {
  const results: AssertionOutput[] = [];

  // Only apply to cross-boundary connections
  if (!connection?.crossBoundary) return results;

  const prefix = `boundary-${stepIndex}`;

  // Check productSource is present
  if (!event.productSource) {
    results.push({
      assertionId: `${prefix}-source`,
      assertionType: 'boundary_crossing',
      passed: false,
      detail: `Step ${stepIndex}: cross-boundary event missing productSource`,
    });
  } else {
    results.push({
      assertionId: `${prefix}-source`,
      assertionType: 'boundary_crossing',
      passed: true,
      detail: `Step ${stepIndex}: productSource "${event.productSource}" present`,
    });
  }

  // Check causation chain integrity
  if (previousEvent) {
    // Note: causation isn't always strictly the immediate predecessor,
    // so we check that causationEventIds is non-empty for cross-boundary
    if (event.causationEventIds.length === 0 && stepIndex > 0) {
      results.push({
        assertionId: `${prefix}-causation`,
        assertionType: 'boundary_crossing',
        passed: false,
        detail: `Step ${stepIndex}: cross-boundary event has empty causationEventIds`,
      });
    } else {
      results.push({
        assertionId: `${prefix}-causation`,
        assertionType: 'boundary_crossing',
        passed: true,
        detail: `Step ${stepIndex}: causation chain present (${event.causationEventIds.length} refs)`,
      });
    }
  }

  // Check relationship field if connection has one
  if (connection.relationship) {
    results.push({
      assertionId: `${prefix}-relationship`,
      assertionType: 'boundary_crossing',
      passed: true,
      detail: `Step ${stepIndex}: boundary relationship "${connection.relationship}" declared`,
    });
  }

  return results;
}

// ---------------------------------------------------------------------------
// Tier 3 — Schema Contract Assertions
// ---------------------------------------------------------------------------

/** Validates that event payload satisfies the schema contract on a connection. */
export function assertSchemaContract(
  stepIndex: number,
  event: PlatformEvent,
  connection: ConnectionDef | null,
): AssertionOutput[] {
  const results: AssertionOutput[] = [];

  if (!connection?.schemaContract) return results;

  const contract = connection.schemaContract;
  const payload = event.payload as Record<string, unknown>;
  const prefix = `schema-${stepIndex}-${contract.contractId}`;

  // Check required fields
  for (const field of contract.requiredFields) {
    const hasField = payload[field] !== undefined && payload[field] !== null;
    if (hasField) {
      results.push({
        assertionId: `${prefix}-field-${field}`,
        assertionType: 'schema_contract',
        passed: true,
        detail: `Step ${stepIndex}: required field "${field}" present`,
      });
    } else {
      results.push({
        assertionId: `${prefix}-field-${field}`,
        assertionType: 'schema_contract',
        passed: false,
        detail: `Step ${stepIndex}: required field "${field}" missing from payload`,
      });
    }
  }

  // Check field types if specified. Moment schema contracts use a domain
  // vocabulary (UUID, DateTime, …) on top of JS primitives — map known
  // domain types to their primitive representation; unknown declared types
  // produce an explicit skip, never a false FAIL.
  if (contract.fieldTypes) {
    for (const [field, declaredType] of Object.entries(contract.fieldTypes)) {
      const value = payload[field];
      if (value === undefined || value === null) continue; // skip missing (covered above)

      const expectedType = normalizeDeclaredType(declaredType);
      if (expectedType === null) {
        results.push(
          skipAssertion(
            `${prefix}-type-${field}`,
            'schema_contract',
            `Step ${stepIndex}: declared type "${declaredType}" for field "${field}" is not a verifiable primitive — type check skipped`,
          ),
        );
        continue;
      }

      const actualType = Array.isArray(value) ? 'array' : typeof value;
      if (actualType === expectedType) {
        results.push({
          assertionId: `${prefix}-type-${field}`,
          assertionType: 'schema_contract',
          passed: true,
          detail: `Step ${stepIndex}: field "${field}" type "${actualType}" matches expected "${declaredType}"`,
        });
      } else {
        results.push({
          assertionId: `${prefix}-type-${field}`,
          assertionType: 'schema_contract',
          passed: false,
          detail: `Step ${stepIndex}: field "${field}" type "${actualType}" does not match expected "${declaredType}" (${expectedType})`,
        });
      }
    }
  }

  return results;
}

/**
 * Map a schema-contract declared type to the JS primitive it is carried as.
 * Moment contracts mix JS primitives with domain types (UUID, DateTime, …)
 * that are serialized as strings. Unknown vocabulary returns null so the
 * caller can emit a skip instead of a false FAIL.
 */
function normalizeDeclaredType(declaredType: string): string | null {
  const lower = declaredType.toLowerCase();
  if (['string', 'number', 'boolean', 'array', 'object'].includes(lower)) return lower;
  if (['uuid', 'datetime', 'date', 'timestamp', 'iso8601', 'id', 'email', 'url'].includes(lower)) {
    return 'string';
  }
  if (['integer', 'float', 'decimal'].includes(lower)) return 'number';
  return null;
}

// ---------------------------------------------------------------------------
// Branch Evaluation Assertions
// ---------------------------------------------------------------------------

/**
 * Validates branch selection by comparing two INDEPENDENT observations:
 * the predicate evaluated against the raw event payload (what the event says
 * happened) vs the branch marker encoded in the taken node's ID suffix
 * (`::brN` — which branch the walker actually landed on). Route-key order in
 * the predicate defines the marker indices (first route condition → br0).
 *
 * This replaces the previous circular check, which recomputed the same
 * expression the walker used and compared it against itself.
 */
export function assertBranchEvaluation(
  stepIndex: number,
  nodeId: string,
  event: PlatformEvent,
  predicate: BranchPredicate | undefined,
): AssertionOutput[] {
  if (!predicate) return [];

  const assertionId = `branch-eval-${stepIndex}-route`;
  const payload = event.payload as Record<string, unknown>;
  const raw = payload[predicate.field];

  if (raw === undefined || raw === null) {
    return [
      skipAssertion(
        assertionId,
        'branch_evaluation',
        `Step ${stepIndex}: event "${event.eventType}" payload has no "${predicate.field}" field (old-format artifact) — branch evaluation skipped`,
      ),
    ];
  }

  const marker = branchMarkerOf(nodeId);
  if (marker === null || !/^br\d+$/.test(marker)) {
    return [
      skipAssertion(
        assertionId,
        'branch_evaluation',
        `Step ${stepIndex}: taken node "${nodeId}" carries no branch marker (::brN) — branch selection not verifiable`,
      ),
    ];
  }

  const condition = String(raw);
  const conditions = Object.keys(predicate.routes);
  const conditionIndex = conditions.indexOf(condition);

  if (conditionIndex === -1) {
    return [
      {
        assertionId,
        assertionType: 'branch_evaluation',
        passed: false,
        detail: `Step ${stepIndex}: event outcome "${condition}" is not a declared route condition (declared: ${conditions.join(', ')})`,
      },
    ];
  }

  const expectedMarker = `br${conditionIndex}`;
  if (marker === expectedMarker) {
    return [
      {
        assertionId,
        assertionType: 'branch_evaluation',
        passed: true,
        detail: `Step ${stepIndex}: outcome "${condition}" (route ${conditionIndex}) matches taken branch "${marker}" of node "${nodeId}"`,
      },
    ];
  }

  return [
    {
      assertionId,
      assertionType: 'branch_evaluation',
      passed: false,
      detail: `Step ${stepIndex}: outcome "${condition}" implies branch "${expectedMarker}" but the taken node "${nodeId}" is on branch "${marker}"`,
    },
  ];
}

// ---------------------------------------------------------------------------
// Terminal State Assertions
// ---------------------------------------------------------------------------

/**
 * Terminal tier — falsifiable in both directions:
 * - a terminal-marked node visited before the last path step FAILS
 *   (flow continued past a declared end state);
 * - a last step whose node is not terminal-marked FAILS.
 *
 * Exemption: specs may legitimately omit the [terminal] declaration on
 * flow-final `event` nodes (old-format artifacts always do; new-format specs
 * may simply not declare one), so a non-terminal last node of type `event`
 * yields an explicit SKIPPED record rather than a false FAIL — never silence.
 */
export function assertTerminalState(
  stepIndex: number,
  node: NodeDef,
  isLastStep: boolean,
  terminatedByDeclaredFailure = false,
): AssertionOutput[] {
  const prefix = `terminal-${stepIndex}`;

  if (node.terminal) {
    if (isLastStep) {
      return [
        {
          assertionId: `${prefix}-final`,
          assertionType: 'terminal_state',
          passed: true,
          detail: `Step ${stepIndex}: terminal node "${node.id}" is the last step in the path`,
        },
      ];
    }
    return [
      {
        assertionId: `${prefix}-premature`,
        assertionType: 'terminal_state',
        passed: false,
        detail: `Step ${stepIndex}: terminal node "${node.id}" was visited but is not the last path step — flow continued past a terminal state`,
      },
    ];
  }

  if (isLastStep) {
    // A run whose next event is SimFailure.* aborts exactly where the scenario
    // declared it would (a precondition refusal in a negative scenario): the
    // refused node is legitimately non-terminal in the topology.
    if (terminatedByDeclaredFailure) {
      return [
        {
          assertionId: `${prefix}-declared-failure`,
          assertionType: 'terminal_state',
          passed: true,
          detail: `Step ${stepIndex}: run terminated at "${node.id}" by a declared precondition violation (SimFailure) — expected abort point`,
        },
      ];
    }
    if (node.type === 'event') {
      return [
        skipAssertion(
          `${prefix}-unterminated`,
          'terminal_state',
          `Step ${stepIndex}: last node "${node.id}" (type "event") is not declared [terminal] in the spec — flow-final events without a terminal marker are skipped, not failed`,
        ),
      ];
    }
    return [
      {
        assertionId: `${prefix}-unterminated`,
        assertionType: 'terminal_state',
        passed: false,
        detail: `Step ${stepIndex}: last path node "${node.id}" (type "${node.type}") is not marked as terminal`,
      },
    ];
  }

  return [];
}

// ---------------------------------------------------------------------------
// Aggregate runner — convenience for calling all applicable assertions on a step
// ---------------------------------------------------------------------------

export interface StepAssertionContext {
  /** PATH-CURSOR index (position within expectedPath), NOT the raw event index. */
  stepIndex: number;
  nodeId: string;
  node: NodeDef | undefined;
  event: PlatformEvent;
  previousEvent: PlatformEvent | null;
  connection: ConnectionDef | null;
  /**
   * When set, the boundary/schema connection could not be resolved
   * (old-format topology without per-connection eventType) — the tiers emit
   * explicit SKIPPED records instead of staying silent.
   */
  connectionSkipReason?: string;
  expectedPath: string[];
  predicate: BranchPredicate | undefined;
  /** When set, the node's frame is a branch frame but carries no predicate. */
  predicateSkipReason?: string;
  /**
   * When set, node resolution fell back to expectedPath — the path-step
   * comparison would be vacuous, so it is emitted as SKIPPED with this reason.
   */
  resolutionFallbackReason?: string;
  isLastStep: boolean;
  totalSteps: number;
  /** The event following this one is SimFailure.* — the run aborts here by
   *  the scenario's own declaration (negative-scenario precondition refusal). */
  terminatedByDeclaredFailure?: boolean;
}

export function runAllAssertionsForStep(ctx: StepAssertionContext): AssertionOutput[] {
  const results: AssertionOutput[] = [];

  // Tier 1: path — the observed node comes from the event (payload.nodeId /
  // eventRouting), so this is a real, falsifiable comparison. When resolution
  // had to fall back to expectedPath itself, the comparison is circular and
  // is surfaced as an explicit SKIPPED record instead.
  if (ctx.resolutionFallbackReason) {
    results.push(
      skipAssertion(
        `path-step-${ctx.stepIndex}`,
        'path',
        `Step ${ctx.stepIndex}: resolution-fallback — ${ctx.resolutionFallbackReason}`,
      ),
    );
  } else {
    results.push(assertPathStep(ctx.stepIndex, ctx.nodeId, ctx.expectedPath));
  }

  // Tier 2 + 3: boundary crossing and schema contract on the resolved
  // frame-level connection. Unresolvable connection → explicit skips.
  if (ctx.connection) {
    results.push(
      ...assertBoundaryCrossing(ctx.stepIndex, ctx.event, ctx.previousEvent, ctx.connection),
    );
    results.push(...assertSchemaContract(ctx.stepIndex, ctx.event, ctx.connection));
  } else if (ctx.connectionSkipReason) {
    results.push(
      skipAssertion(
        `boundary-${ctx.stepIndex}-connection`,
        'boundary_crossing',
        `Step ${ctx.stepIndex}: ${ctx.connectionSkipReason}`,
      ),
    );
    results.push(
      skipAssertion(
        `schema-${ctx.stepIndex}-connection`,
        'schema_contract',
        `Step ${ctx.stepIndex}: ${ctx.connectionSkipReason}`,
      ),
    );
  }

  // Branch evaluation — independent comparison of event outcome vs taken
  // branch marker. Branch frame without predicate → explicit skip.
  if (ctx.predicate) {
    results.push(...assertBranchEvaluation(ctx.stepIndex, ctx.nodeId, ctx.event, ctx.predicate));
  } else if (ctx.predicateSkipReason) {
    results.push(
      skipAssertion(
        `branch-eval-${ctx.stepIndex}-route`,
        'branch_evaluation',
        `Step ${ctx.stepIndex}: ${ctx.predicateSkipReason}`,
      ),
    );
  }

  // Terminal state
  if (ctx.node) {
    results.push(
      ...assertTerminalState(
        ctx.stepIndex,
        ctx.node,
        ctx.isLastStep,
        ctx.terminatedByDeclaredFailure ?? false,
      ),
    );
  } else {
    results.push(
      skipAssertion(
        `terminal-${ctx.stepIndex}-node`,
        'terminal_state',
        `Step ${ctx.stepIndex}: node "${ctx.nodeId}" not found in topology — terminal check skipped`,
      ),
    );
  }

  return results;
}
